import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { catchError, map, Observable, of, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AuthResponse, AuthUser } from './auth.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly user = signal<AuthUser | null>(null);
  private accessToken: string | null = null;
  private initialized = false;
  private initializeRequest?: Observable<boolean>;

  constructor(private readonly http: HttpClient) {}

  login(returnUrl = '/'): void {
    const target = `${environment.securityApiUrl}/api/auth/sso/start?returnUrl=${encodeURIComponent(returnUrl || '/')}`;
    window.location.assign(target);
  }

  initialize(): Observable<boolean> {
    if (this.initialized) return of(this.isAuthenticated());
    this.initializeRequest ??= this.http.get<AuthResponse>(`${environment.securityApiUrl}/api/auth/session`, { withCredentials: true }).pipe(
      tap((response) => this.setState(response)),
      tap(() => (this.initialized = true)),
      map(() => true),
      catchError(() => {
        this.initialized = true;
        this.clearState();
        return of(false);
      })
    );
    return this.initializeRequest ?? of(false);
  }

  refresh(): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${environment.securityApiUrl}/api/auth/session/refresh`, {}, { withCredentials: true }).pipe(tap((response) => this.setState(response)));
  }

  logout(): Observable<void> {
    return this.http.post<void>(`${environment.securityApiUrl}/api/auth/logout`, {}, { withCredentials: true }).pipe(
      tap(() => this.clearState()),
      catchError((error) => {
        this.clearState();
        return throwError(() => error);
      })
    );
  }

  getToken(): string | null { return this.accessToken; }
  isAuthenticated(): boolean { return this.accessToken !== null; }

  private setState(response: AuthResponse): void {
    this.accessToken = response.accessToken;
    this.user.set(response.user);
  }

  private clearState(): void {
    this.accessToken = null;
    this.user.set(null);
  }
}
